import type {
    ClientBtpEnvResponse,
    ClientDetailModel,
    ClientDocResponse,
    ClientErpEnvResponse,
    ClientModel,
    ClientResponse,
    CreateBtpEnvParams,
    CreateClientParams,
    CreateErpEnvParams,
    UpdateBtpEnvParams,
    UpdateClientParams,
    UpdateErpEnvParams,
    UploadClientDocumentParams
} from '@/domain/models/client.js';
import { ClientDetailModel as ClientDetailModelClass, ClientModel as ClientModelClass } from '@/domain/models/client.js';
import type { HttpClient } from '@/domain/protocols/http-client.js';
import type { ClientRepository } from '@/domain/repositories/client-repository.js';

export class ClientRepositoryImpl implements ClientRepository {
    constructor(private readonly httpClient: HttpClient) {}

    public async findAll(search?: string): Promise<ClientModel[]> {
        const qs = search ? `?search=${encodeURIComponent(search)}` : '';
        const response = await this.httpClient.request<{ data: ClientResponse[] }>({ url: `/api/clients${qs}`, method: 'GET' });
        return ClientModelClass.withFromResponseList(response.body.data);
    }

    public async findById(id: string): Promise<ClientDetailModel> {
        const response = await this.httpClient.request<ClientResponse & { environmentsErp: ClientErpEnvResponse[]; environmentsBtp: ClientBtpEnvResponse[] }>({
            url: `/api/clients/${encodeURIComponent(id)}`,
            method: 'GET'
        });
        return ClientDetailModelClass.withFromResponse(response.body as ClientDetailModel);
    }

    public async create(params: CreateClientParams): Promise<ClientModel> {
        const body = {
            name: params.name,
            abbreviation: params.abbreviation,
            aiModel: params.aiModel,
            slaConfig: {
                slaHoursEstimateEf: params.slaHoursEstimateEf,
                slaHoursStartDev: params.slaHoursStartDev,
                slaHoursAnalyzeBug: params.slaHoursAnalyzeBug,
                slaHoursAnalyzeMinor: params.slaHoursAnalyzeMinor,
                slaDaysValidateDoc: params.slaDaysValidateDoc,
                slaMinScoreAi: params.slaMinScoreAi,
                aiStrictnessLevel: params.aiStrictnessLevel,
                aiExtraSystemPrompt: params.aiExtraSystemPrompt
            },
            emailDomains: params.emailDomains,
            themeColors: params.themeColors
        };
        const response = await this.httpClient.request<ClientResponse>({ url: '/api/clients', method: 'POST', body });
        return ClientModelClass.withFromResponse(response.body);
    }

    public async update(id: string, params: UpdateClientParams): Promise<ClientModel> {
        const body = {
            name: params.name,
            aiModel: params.aiModel,
            slaConfig: {
                slaHoursEstimateEf: params.slaHoursEstimateEf,
                slaHoursStartDev: params.slaHoursStartDev,
                slaHoursAnalyzeBug: params.slaHoursAnalyzeBug,
                slaHoursAnalyzeMinor: params.slaHoursAnalyzeMinor,
                slaDaysValidateDoc: params.slaDaysValidateDoc,
                slaMinScoreAi: params.slaMinScoreAi,
                aiStrictnessLevel: params.aiStrictnessLevel,
                aiExtraSystemPrompt: params.aiExtraSystemPrompt
            },
            emailDomains: params.emailDomains,
            themeColors: params.themeColors
        };
        const response = await this.httpClient.request<ClientResponse>({ url: `/api/clients/${encodeURIComponent(id)}`, method: 'PUT', body });
        return ClientModelClass.withFromResponse(response.body);
    }

    public async uploadLogo(clientId: string, params: { fileData: string; fileName: string; mimeType: string }): Promise<{ logoUrl: string }> {
        const response = await this.httpClient.request<{ logoUrl: string }>({ url: `/api/clients/${encodeURIComponent(clientId)}/logo`, method: 'POST', body: params });
        return response.body;
    }

    public async removeLogo(clientId: string): Promise<void> {
        await this.httpClient.request({ url: `/api/clients/${encodeURIComponent(clientId)}/logo`, method: 'DELETE' });
    }

    public async listErpEnvironments(clientId: string): Promise<ClientErpEnvResponse[]> {
        const response = await this.httpClient.request<ClientErpEnvResponse[]>({ url: `/api/clients/${encodeURIComponent(clientId)}/environments/erp`, method: 'GET' });
        return response.body;
    }

    public async createErpEnvironment(clientId: string, params: CreateErpEnvParams): Promise<ClientErpEnvResponse> {
        const response = await this.httpClient.request<ClientErpEnvResponse>({
            url: `/api/clients/${encodeURIComponent(clientId)}/environments/erp`,
            method: 'POST',
            body: params
        });
        return response.body;
    }

    public async updateErpEnvironment(envId: string, params: UpdateErpEnvParams): Promise<ClientErpEnvResponse> {
        const response = await this.httpClient.request<ClientErpEnvResponse>({ url: `/api/clients/0/environments/erp/${encodeURIComponent(envId)}`, method: 'PUT', body: params });
        return response.body;
    }

    public async deleteErpEnvironment(envId: string): Promise<void> {
        await this.httpClient.request({ url: `/api/clients/0/environments/erp/${encodeURIComponent(envId)}`, method: 'DELETE' });
    }

    public async listBtpEnvironments(clientId: string): Promise<ClientBtpEnvResponse[]> {
        const response = await this.httpClient.request<ClientBtpEnvResponse[]>({ url: `/api/clients/${encodeURIComponent(clientId)}/environments/btp`, method: 'GET' });
        return response.body;
    }

    public async createBtpEnvironment(clientId: string, params: CreateBtpEnvParams): Promise<ClientBtpEnvResponse> {
        const response = await this.httpClient.request<ClientBtpEnvResponse>({
            url: `/api/clients/${encodeURIComponent(clientId)}/environments/btp`,
            method: 'POST',
            body: params
        });
        return response.body;
    }

    public async updateBtpEnvironment(envId: string, params: UpdateBtpEnvParams): Promise<ClientBtpEnvResponse> {
        const response = await this.httpClient.request<ClientBtpEnvResponse>({ url: `/api/clients/0/environments/btp/${encodeURIComponent(envId)}`, method: 'PUT', body: params });
        return response.body;
    }

    public async deleteBtpEnvironment(envId: string): Promise<void> {
        await this.httpClient.request({ url: `/api/clients/0/environments/btp/${encodeURIComponent(envId)}`, method: 'DELETE' });
    }

    public async listDocuments(clientId: string): Promise<ClientDocResponse[]> {
        const response = await this.httpClient.request<ClientDocResponse[]>({ url: `/api/clients/${encodeURIComponent(clientId)}/documents`, method: 'GET' });
        return response.body;
    }

    public async uploadDocument(clientId: string, params: UploadClientDocumentParams): Promise<ClientDocResponse> {
        const formData = new FormData();
        formData.append('category', params.category);
        formData.append('file', params.file);
        const res = await fetch(`/api/clients/${encodeURIComponent(clientId)}/documents/upload`, {
            method: 'POST',
            body: formData,
            headers: this.authHeaders()
        });
        const data = await res.json();
        if (!res.ok) {
            throw new Error(data?.message ?? 'Upload failed');
        }
        return data as ClientDocResponse;
    }

    public async deleteDocument(clientId: string, docId: string): Promise<void> {
        await this.httpClient.request({ url: `/api/clients/${encodeURIComponent(clientId)}/documents/${encodeURIComponent(docId)}`, method: 'DELETE' });
    }

    public async getDocumentDownloadUrl(clientId: string, docId: string): Promise<{ url: string }> {
        const response = await this.httpClient.request<{ url: string }>({
            url: `/api/clients/${encodeURIComponent(clientId)}/documents/${encodeURIComponent(docId)}/download-url`,
            method: 'GET'
        });
        return response.body;
    }

    private authHeaders(): Record<string, string> {
        const token = localStorage.getItem('accessToken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    }
}
